import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FirFdiiQbaiExcludeAcctService } from './fir-fdii-qbai-exclude-acct.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-fir-fdii-qbai-exclude-acct',
    templateUrl: './fir-fdii-qbai-exclude-acct.component.html',
    styleUrls: ['./fir-fdii-qbai-exclude-acct.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FirFdiiQbaiExcludeAcctComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  savingFlag = false;

  // Component specific properties
  selectedData: any[] = [];
  gridDataMultipleSelect: any[] = [];
  isExcludedWarning = false;
  isincludedWarning = false;
  isExcludedWarningMsgs: string | null = null;
  modal_name: string = '';
  modalTitle = "Exclude/Include Accounts";

  // Entity data properties
  entityList: any[] = [];
  rowData: any = {};
  colData: any = {};
  gridData: any = {};
  filterParams: any = {};
  baseURLs: any = {};

  // Global settings
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private componentService: FirFdiiQbaiExcludeAcctService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.globalService = header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
    this.modal_name =
      activeScreen?.data?.modalName || activeScreen?.data?.modal_name || '';
    this.processGridDataIfReady();
  }

  /** Matches former @Input('grid-data') / @Input('modal-name') behavior before calling processGridData. */
  private processGridDataIfReady(): void {
    const data = this.gridData;
    if (data && Array.isArray(data) && data.length > 0 && this.modal_name) {
      this.processGridData();
    }
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
      }
    } catch (e) {
      console.error('Error in FirFdiiQbaiExcludeAcct init:', e);
    }
  }

  private setupEntityData() {
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  processGridData() {
    if (this.gridData != null && this.gridData != undefined && this.modal_name) {
      const tempGridData = _.clone(this.gridData);
      this.gridDataMultipleSelect = _.clone(tempGridData);
      this.selectedData = [];

      // Logic to Display Warning Messages
      this.isExcludedWarning = false;
      this.isincludedWarning = false;

      for (let index in this.gridDataMultipleSelect) {
        if (this.gridDataMultipleSelect[index].data.IS_INCLUDE_VAL === 0 && this.modal_name == 'excludeAcct') {
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS"] = 'Already Excluded';
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS_FLAG"] = 1;
          this.isExcludedWarning = true;
        }
        else if (this.gridDataMultipleSelect[index].data.IS_INCLUDE_VAL === 1 && this.modal_name == 'includeAcct') {
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS"] = 'Already Included';
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS_FLAG"] = 1;
          this.isincludedWarning = true;
        } else {
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS"] = '';
          this.gridDataMultipleSelect[index].data["INC_EXC_STATUS_FLAG"] = 0;
        }

        this.selectedData.push(this.gridDataMultipleSelect[index].data);
      }

      if (this.isExcludedWarning || this.isincludedWarning) {
        this.isExcludedWarningMsgs = "Please resolve the warnings before Save";
      }
    }
  }

  removeRow(data: any) {
    const index = this.selectedData.indexOf(data);
    this.selectedData.splice(index, 1);

    this.isExcludedWarning = false;
    this.isincludedWarning = false;
    this.isExcludedWarningMsgs = null;

    for (let index in this.selectedData) {
      if (this.selectedData[index].IS_INCLUDE_VAL === 0 && this.modal_name == 'excludeAcct')
        this.isExcludedWarning = true;

      if (this.selectedData[index].IS_INCLUDE_VAL === 1 && this.modal_name == 'includeAcct')
        this.isincludedWarning = true;
    }

    if (this.isExcludedWarning || this.isincludedWarning) {
      this.isExcludedWarningMsgs = "Please resolve the warnings before Save";
    }
  }

  save() {
    this.isSaveClicked = true;
    this.savingFlag = true;
    this.crudLoading = true;

    if (this.selectedData.length == 0) {
      this.isSaveClicked = false;
      this.savingFlag = false;
      this.crudLoading = false;
      this.gtwSnackbarService.openSnackBar(
        'Please select atleast one row for Saving',
        'gtw-snackbar--red'
      );
      return;
    }

    const jsonObjDetails = [];
    for (let index in this.selectedData) {
      const jsonObj = {
        "combination_key": this.selectedData[index].COMBINATION_KEY,
        "local_acct_key": this.selectedData[index].LOCAL_ACCT_KEY,
        "local_chart_key": this.selectedData[index].LOCAL_CHART_KEY,
        "is_include": (this.modal_name === 'excludeAcct' ? 0 : 1),
      };
      jsonObjDetails.push(jsonObj);
    }

    const message = (this.modal_name == 'excludeAcct' ? 'Selected accounts have been Excluded' : 'Selected accounts have been Included');

    this.componentService.saveFdiiQbaiAcctExclude(
      this.GENERAL_CONFIG.base_url,
      jsonObjDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.savingFlag = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(
            response.errorMessage,
            'gtw-snackbar--red'
          );
        } else {
          this.gtwSnackbarService.openSnackBar(
            message,
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.savingFlag = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  getSortedData() {
    return this.selectedData.sort((a, b) => {
      if (a.INC_EXC_STATUS < b.INC_EXC_STATUS) return -1;
      if (a.INC_EXC_STATUS > b.INC_EXC_STATUS) return 1;
      return 0;
    });
  }
}
